import type {
  OrderCompany,
  OrderParticipant,
  OrderTag,
  OrderType,
  PurchaseOrderLine,
  ResponsibleUser,
  SalesOrder,
  SalesOrderAttachment,
} from '@/api/orders/types';

// ─── Constants ───────────────────────────────────────────────────────────────

export const TRACING_ORDER_STATUS_OPTIONS = [
  { key: 'IN_PROGRESS', label: 'status.IN_PROGRESS' },
  { key: 'IN_REVIEW', label: 'status.IN_REVIEW' },
  { key: 'COMPLETED', label: 'status.COMPLETED' },
] as const;

export const TRACING_ORDER_STATUS_UNSET_FILTER_OPTION = {
  key: 'N/A',
  label: 'status.N/A',
} as const;

export const PRODUCTION_PROCESS_OPTIONS = [
  { key: 'COMPLIANCE', label: 'process.COMPLIANCE' },
  { key: 'FABRIC_PROCESSING', label: 'process.FABRIC_PROCESSING' },
  { key: 'LEATHER_TANNING', label: 'process.LEATHER_TANNING' },
  { key: 'MANUFACTURING', label: 'process.MANUFACTURING' },
  { key: 'MATERIAL_RECYCLING', label: 'process.MATERIAL_RECYCLING' },
  { key: 'PACKAGING', label: 'process.PACKAGING' },
  { key: 'RAW_MATERIAL_CREATION', label: 'process.RAW_MATERIAL_CREATION' },
  { key: 'RAW_MATERIAL_PROCESSING', label: 'process.RAW_MATERIAL_PROCESSING' },
  { key: 'RETAILER', label: 'process.RETAILER' },
  { key: 'SOURCING_AND_IMPORT', label: 'process.SOURCING_AND_IMPORT' },
  { key: 'TRIMS_AND_COMPONENTS', label: 'process.TRIMS_AND_COMPONENTS' },
  { key: 'WAREHOUSE', label: 'process.WAREHOUSE' },
  { key: 'YARN_PROCESSING', label: 'process.YARN_PROCESSING' },
] as const;

export const AI_SUMMARY_TYPES = {
  DOC_TYPE: 'docType',
  DOC_CONTENT: 'docContent',
  SC_ANALYSIS: 'scAnalysis',
} as const;

export const TRACE_TYPE = {
  SALES_ORDER: 'SALES_ORDER',
  PURCHASE_ORDER: 'PURCHASE_ORDER',
  INTERNAL_ORDER: 'INTERNAL_ORDER',
} as const;

export const STATUS_VARIANT: Record<
  string,
  'success' | 'warning' | 'info' | 'muted'
> = {
  IN_PROGRESS: 'info',
  IN_REVIEW: 'warning',
  COMPLETED: 'success',
};

// ─── Create Sales Order Form ──────────────────────────────────────────────────

export interface CreateSalesOrderLine {
  localId: string;
  styleId: string;
  styleName: string;
  styleCode: string;
  styleTypeName: string;
  unit: string;
  quantity: number | null;
  secondaryQuantity: number | null;
  isComponent?: boolean;
  quantityUnits?: string[];
  /** Set for product-mapping lines, which may sit at any of the three levels.
   *  `owningStyleName` is the style it belongs to, however deep it sits. */
  productLevel?: ProductLevel;
  owningStyleName?: string | null;
}

export interface SalesOrderFormValues {
  supplierId: string;
  supplierFacilityProcess: string;
  buyerId: string;
  buyerFacilityProcess: string;
  isReceiverSameAsBuyer: boolean;
  receiverId: string;
  receiverFacilityProcess: string;
  responsibleUserIds: string[];
  orderNumber: string;
  orderType: string;
  orderDate: string | null;
  agreedDeliveryDate: string | null;
  orderTags: string[];
  hasAcceptedProductWarning: boolean;
  attachmentTemplateId: string;
  documents: Array<{
    type: string;
    name: string;
    file?: File;
    fileId?: string;
    url?: string;
    documentId?: string;
  }>;
}

export type OrderTagsResponse = OrderTag[];

export interface OrderTypesResponse {
  data: OrderType[];
}

export type ProductLevel = 'style' | 'variant' | 'sku';

export interface StyleSearchResult {
  id: string;
  name: string;
  codes: Array<{ codeId: string; codeValue: string }>;
  quantityUnits: string[];
  typeName: string;
  label: string;
  isComponent?: boolean;
  /** Set when the picker is sectioned by style / variant / SKU.
   *  `owningStyleName` is the style it belongs to, however deep it sits. */
  productLevel?: ProductLevel;
  owningStyleName?: string | null;
}

// ─── Template Form Shapes ──────────────────────────────────────────────────────

export interface TemplateDocumentFormValues {
  type: string;
  isMandatory: boolean;
  countryCodes: string[];
}

export interface TemplateRuleFormValues {
  facilityProcesses: string[];
  documents: TemplateDocumentFormValues[];
}

export interface AttachmentTemplateFormValues {
  name: string;
  description: string;
  tracingType: string;
  rules: TemplateRuleFormValues[];
  backupRule: TemplateRuleFormValues | null;
}

// ─── PO Update Payload (PUT) ─────────────────────────────────────────────────

/** Patch to apply on top of the current order — hook merges this with existing data. */
export interface PatchPurchaseOrderDetailPayload {
  orderNumber?: string;
  status?: string;
  orderDate?: string | null;
  agreedDeliveryDate?: string | null;
  deliveryDate?: string | null;
  tracingAttachmentTemplateId?: string | null;
  orderTypeId?: string | null;
  orderTags?: string[];
  buyer?: { id: string; facilityProcesses: string[] };
  supplier?: { id: string; facilityProcesses: string[] };
  receiver?: { id: string; facilityProcesses: string[] } | null;
  lines?: PurchaseOrderLine[];
  responsibleUserIds?: string[];
}

// ─── Sales Order Detail ───────────────────────────────────────────────────────

export interface SalesOrderDetailLineProduct {
  id: string;
  name?: string | null;
  code?: string;
  styleTypeId?: string | null;
  styleLevelId?: string | null;
  /** new TS backend */
  styleTypeName?: string | null;
  styleTypeQuantityUnits?: string[] | null;
  /** new TS backend */
  styleLevelName?: string | null;
  /** legacy JS backend */
  productCategoryName?: string | null;
  /** legacy JS backend */
  productLevelName?: string | null;
  codes?: Array<{ codeId: string; codeValue: string }>;
  /** A line may reference a style, a variant or a SKU. */
  productLevel?: ProductLevel | null;
  /** variant → parent is its style; sku → parent is its variant and
   *  grandParent its style. The style it belongs to is
   *  `grandParentName ?? parentName`. */
  parentName?: string | null;
  grandParentName?: string | null;
}

export interface SalesOrderDetailLine {
  id: string;
  style?: SalesOrderDetailLineProduct | null;
  product?: SalesOrderDetailLineProduct | null;
  isOwnStyleLinked?: boolean;
  isStyleLinked?: boolean;
  quantity?: number | null;
  unit?: string | null;
  secondaryQuantity?: number | null;
  secondaryUnit?: string | null;
  /** Legacy field — may be returned by older API responses */
  styleId?: string | null;
  /** Legacy field — may be returned by older API responses */
  deprecatedStyleId?: string | null;
  /** Legacy field — may be returned by older API responses */
  deprecatedProductId?: string | null;
}

export interface SalesOrderCompanyDetail extends OrderCompany {
  facilityProcesses?: string | string[];
}

/** Full SO detail shape — SalesOrder + extra fields returned by single-fetch */
export interface SalesOrderDetail extends Omit<SalesOrder, 'lines'> {
  orderType?: string;
  orderDate?: string;
  orderNumber?: string;
  orderTags?: string[];
  tracingOrderTags?: string;
  responsibleUsers?: ResponsibleUser[];
  salesOrderResponsibleUsers?: ResponsibleUser[];
  buyer?: SalesOrderCompanyDetail;
  supplier?: SalesOrderCompanyDetail;
  receiver?: SalesOrderCompanyDetail | null;
  lines?: SalesOrderDetailLine[];
  participants?: OrderParticipant[];
  attachments?: SalesOrderAttachment[];
  transactions?: Array<{ id: string; tracingLinkId?: string | null }> | null;
  createdAt?: string;
  createdByUserName?: string;
  createdByCompanyName?: string;
  supplierCompanyId?: string;
  destinationTracingNode?: {
    id?: string;
    assetId?: string;
    featuredFacilityProcess?: string | { id?: string } | null;
    facilityProcesses?: string | string[] | null;
    assetCountry?: string | null;
  } | null;
  originTracingNode?: {
    id?: string;
    assetId?: string;
    featuredFacilityProcess?: string | { id?: string } | null;
    facilityProcesses?: string | string[] | null;
    assetCountry?: string | null;
  } | null;
  receiverTracingNode?: {
    id?: string;
    assetId?: string;
    featuredFacilityProcess?: string | { id?: string } | null;
    facilityProcesses?: string | string[] | null;
    assetCountry?: string | null;
  } | null;
  /** Legacy field — may be present in older API responses */
  salesOrderNumber?: string;
  /** Legacy field — company with facilityProcesses, may be present in older API responses */
  buyerCompany?: SalesOrderCompanyDetail;
  /** Legacy field — company with facilityProcesses, may be present in older API responses */
  supplierCompany?: SalesOrderCompanyDetail;
  /** Legacy field — attachment template reference, may be present in older API responses */
  attachmentTemplate?: { id: string; name?: string } | null;
  /** Whether the current user's company can remove this order */
  isRemovable?: boolean;
  /** Whether this order is linked to another order */
  isLinkedOrder?: boolean;
}

export interface PatchSalesOrderDetailPayload {
  orderNumber?: string;
  status?: string;
  /** Human-readable type name — looked up to tracingOrderTypeId */
  orderType?: string;
  /** Type UUID — used when calling TS backend PUT */
  orderTypeId?: string | null;
  orderDate?: string | null;
  agreedDeliveryDate?: string | null;
  deliveryDate?: string | null;
  orderTags?: string[];
  tracingAttachmentTemplateId?: string;
  buyer?: { id: string; facilityProcesses: string[] };
  supplier?: { id: string; facilityProcesses: string[] };
  receiver?: { id: string; facilityProcesses: string[] } | null;
  responsibleUserIds?: string[] | null;
  lines?: Array<{
    styleId?: string;
    quantity?: number;
    unit?: string;
    secondaryQuantity?: number;
    secondaryUnit?: string;
  }>;
}
